import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  url: string;
  type: 'page' | 'project' | 'blog';
  image?: string;
}

interface SiteSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SiteSearch({ isOpen, onClose }: SiteSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const searchContent = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searchResults: SearchResult[] = [];

        // Search blog posts
        const { data: blogPosts } = await supabase
          .from('blog_posts')
          .select('id, title, excerpt, slug, featured_image')
          .eq('status', 'published')
          .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,content.ilike.%${query}%`)
          .limit(5);

        if (blogPosts) {
          searchResults.push(...blogPosts.map(post => ({
            id: post.id,
            title: post.title,
            description: post.excerpt || '',
            url: `/blog/${post.slug}`,
            type: 'blog' as const,
            image: post.featured_image
          })));
        }

        // Search gallery projects
        const { data: projects } = await supabase
          .from('systems')
          .select('id, title, description, category, photos')
          .or(`title.ilike.%${query}%,description.ilike.%${query}%,category.ilike.%${query}%`)
          .limit(5);

        if (projects) {
          searchResults.push(...projects.map(project => ({
            id: project.id.toString(),
            title: project.title,
            description: project.description,
            url: `/gallery/${project.id}`,
            type: 'project' as const,
            image: project.photos?.[0]?.url
          })));
        }

        // Add static pages
        const staticPages = [
          { title: 'Services', description: 'Professional audio/video installation services', url: '/services' },
          { title: 'Contact', description: 'Get in touch with our team', url: '/contact' },
          { title: 'For Builders', description: 'Partnership opportunities for contractors', url: '/builders' }
        ].filter(page => 
          page.title.toLowerCase().includes(query.toLowerCase()) ||
          page.description.toLowerCase().includes(query.toLowerCase())
        );

        searchResults.push(...staticPages.map(page => ({
          id: page.url,
          title: page.title,
          description: page.description,
          url: page.url,
          type: 'page' as const
        })));

        setResults(searchResults.slice(0, 8));
        
        // Track search
        trackEvent({
          category: 'Search',
          action: 'Query',
          label: query
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchContent, 300);
    return () => clearTimeout(debounceTimer);
  }, [query, trackEvent]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const result = results[selectedIndex];
      trackEvent({
        category: 'Search',
        action: 'Result Click',
        label: result.title
      });
      window.location.href = result.url;
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleResultClick = (result: SearchResult) => {
    trackEvent({
      category: 'Search',
      action: 'Result Click',
      label: result.title
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      <div className="container mx-auto px-4 pt-20">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center p-4 border-b">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search projects, blog posts, and pages..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-lg outline-none"
            />
            {loading && <Loader2 className="w-5 h-5 text-gray-400 animate-spin mr-3" />}
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Results */}
          <div className="max-h-96 overflow-y-auto">
            {results.length > 0 ? (
              <div className="py-2">
                {results.map((result, index) => (
                  <Link
                    key={result.id}
                    to={result.url}
                    onClick={() => handleResultClick(result)}
                    className={`flex items-center p-4 hover:bg-gray-50 transition-colors ${
                      index === selectedIndex ? 'bg-gray-50' : ''
                    }`}
                  >
                    {result.image && (
                      <img
                        src={result.image}
                        alt={result.title}
                        className="w-12 h-12 object-cover rounded mr-4"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{result.title}</h3>
                      <p className="text-sm text-gray-600 truncate">{result.description}</p>
                      <span className="text-xs text-gray-400 capitalize">{result.type}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : query.length >= 2 && !loading ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No results found for "{query}"</p>
                <p className="text-sm mt-2">Try different keywords or browse our services</p>
              </div>
            ) : query.length < 2 ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Start typing to search...</p>
                <p className="text-sm mt-2">Search projects, blog posts, and pages</p>
              </div>
            ) : null}
          </div>

          {/* Search Tips */}
          <div className="p-4 bg-gray-50 border-t text-xs text-gray-500">
            <div className="flex items-center justify-between">
              <span>Use ↑↓ to navigate, Enter to select, Esc to close</span>
              <span>Powered by intelligent search</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}